import React, { useEffect, useMemo, useRef, useState } from "react";

const firstArt = (...values) => values.find((v) => typeof v === "string" && v.trim()) || "";

const videoArt = (video, channel) => firstArt(
  video?.backdrop_url,
  video?.thumbnail_url,
  video?.poster_url,
  video?.guide_poster_url,
  channel?.guide_poster_url,
  channel?.thumbnail_url
);

const trimTitle = (title, showTitle) => {
  const raw = (title || "Untitled").trim();
  const show = (showTitle || "").trim();
  if (!show) return raw;
  const escaped = show.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return raw
    .replace(new RegExp(`^${escaped}\\s*[-–—:]?\\s*`, "i"), "")
    .replace(/^S\d{1,2}E\d{1,3}\s*[-–—:]?\s*/i, "")
    .trim() || raw;
};

const ALL_EPISODES = "__all__";

const seasonLabel = (season) =>
  Number(season) === 0 ? "SPECIALS" : `SEASON ${season}`;

const runtimeLabel = (durationSec) => {
  const seconds = Number(durationSec);
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  return `${Math.max(1, Math.round(seconds / 60))} MIN`;
};

export default function SeriesInfo({ showTitle, channel, videos = [], currentVideo, onClose, onTune }) {
  const shellRef = useRef(null);
  const currentCardRef = useRef(null);

  // Phase 2.3.81c-r3 - capture wheel input across the full Series Information viewport.
  useEffect(() => {
    const handleWheel = (event) => {
      const shell = shellRef.current;
      if (!shell || Math.abs(event.deltaY) < 0.1) return;
      if (shell.scrollHeight <= shell.clientHeight) return;

      const before = shell.scrollTop;
      shell.scrollTop += event.deltaY;
      if (shell.scrollTop !== before) event.preventDefault();
    };

    window.addEventListener("wheel", handleWheel, { capture: true, passive: false });
    return () => window.removeEventListener("wheel", handleWheel, { capture: true });
  }, []);

  const episodes = useMemo(() => videos
    .filter((v) => (v?.show_title || "").trim() === (showTitle || "").trim())
    .slice()
    .sort((a, b) =>
      (Number(a.season_number ?? 9999) - Number(b.season_number ?? 9999)) ||
      (Number(a.episode_number ?? 9999) - Number(b.episode_number ?? 9999)) ||
      String(a.title || "").localeCompare(String(b.title || ""))
    ), [videos, showTitle]);

  const seasons = useMemo(() => [...new Set(
    episodes.map((v) => v.season_number).filter((v) => v != null)
  )].sort((a, b) => Number(a) - Number(b)), [episodes]);

  const [season, setSeason] = useState(
    currentVideo?.season_number ?? seasons[0] ?? ALL_EPISODES
  );

  const visible = season === ALL_EPISODES
    ? episodes
    : episodes.filter((v) => String(v.season_number) === String(season));

  const currentIndex = useMemo(
    () => episodes.findIndex((v) => v.id === currentVideo?.id),
    [episodes, currentVideo?.id]
  );

  const previousEpisode = currentIndex > 0 ? episodes[currentIndex - 1] : null;
  const nextEpisode =
    currentIndex >= 0 && currentIndex < episodes.length - 1
      ? episodes[currentIndex + 1]
      : null;

  // If playback changes while Series Information is open, follow the newly
  // playing episode back to its season. Unnumbered material falls back to All.
  useEffect(() => {
    if (!currentVideo?.id) return;
    setSeason(currentVideo?.season_number ?? ALL_EPISODES);
  }, [currentVideo?.id, currentVideo?.season_number]);

  // Phase 2.3.82 - bring the currently playing card into view when Series
  // Information opens or when playback moves to another episode.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      currentCardRef.current?.scrollIntoView({
        block: "center",
        inline: "nearest",
        behavior: "auto",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [currentVideo?.id, season]);

  // Only use genuine series-level synopsis metadata here. Channel descriptions
  // and episode overviews are deliberately not substituted for series copy.
  const seriesSynopsis = firstArt(
    currentVideo?.series_overview,
    episodes.find((v) => (v?.series_overview || "").trim())?.series_overview
  );

  const heroArt = firstArt(
    channel?.guide_poster_url,
    channel?.thumbnail_url,
    videoArt(currentVideo, channel),
    videoArt(episodes[0], channel)
  );

  return (
    <div ref={shellRef} className="series-info-shell" data-testid="series-info">
      {heroArt && (
        <div
          className="series-info-backdrop"
          style={{ backgroundImage: `url("${heroArt}")` }}
          aria-hidden="true"
        />
      )}
      <div className="series-info-wash" aria-hidden="true" />

      <div className="series-info-content">
        <header className="series-info-topline">
          <strong className="series-info-brand">scartlead.tv</strong>
          <button type="button" onClick={onClose} data-testid="close-series-info">← BACK TO TV</button>
        </header>

        <section className={`series-info-hero ${seriesSynopsis ? "" : "series-info-hero-no-synopsis"}`}>
          <div className="series-info-kicker">SERIES COLLECTION</div>
          <h1>{showTitle}</h1>
          <div className="series-info-meta">
            {seasons.length > 0 && <span>{seasons.length} SEASON{seasons.length === 1 ? "" : "S"}</span>}
            <span>{episodes.length} EPISODE{episodes.length === 1 ? "" : "S"}</span>
            {channel?.name && <span>{channel.name}</span>}
          </div>
          {seriesSynopsis && <p className="series-info-synopsis">{seriesSynopsis}</p>}
          <div className="series-info-actions">
            {episodes[0] && (
              <button className="series-info-watch" type="button" onClick={() => onTune(episodes[0].id)} data-testid="series-watch-from-start">
                ▶ WATCH FROM THE BEGINNING
              </button>
            )}
            {currentVideo && (
              <button className="series-info-return-current" type="button" onClick={onClose}>
                CONTINUE CURRENT EPISODE
              </button>
            )}
            {previousEpisode && (
              <button
                className="series-info-step"
                type="button"
                onClick={() => onTune(previousEpisode.id)}
                title={trimTitle(previousEpisode.title, showTitle)}
              >
                ← PREVIOUS EPISODE
              </button>
            )}
            {nextEpisode && (
              <button
                className="series-info-step"
                type="button"
                onClick={() => onTune(nextEpisode.id)}
                title={trimTitle(nextEpisode.title, showTitle)}
              >
                NEXT EPISODE →
              </button>
            )}
          </div>
        </section>

        <section className="series-info-library">
          <div className="series-info-library-head">
            <div>
              <div className="series-info-section-kicker">BROWSE THE SERIES</div>
              <h2>EPISODES</h2>
            </div>
            {seasons.length > 1 && (
              <div className="series-info-seasons" aria-label="Seasons">
                <button
                  type="button"
                  className={season === ALL_EPISODES ? "active" : ""}
                  onClick={() => setSeason(ALL_EPISODES)}
                >
                  ALL EPISODES
                </button>
                {seasons.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={String(season) === String(s) ? "active" : ""}
                    onClick={() => setSeason(s)}
                  >
                    {seasonLabel(s)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="series-info-grid">
            {visible.map((v) => {
              const art = videoArt(v, channel);
              const current = currentVideo?.id === v.id;
              return (
                <button
                  key={v.id}
                  ref={current ? currentCardRef : null}
                  type="button"
                  className={`series-info-episode ${current ? "current" : ""}`}
                  onClick={() => onTune(v.id)}
                  data-testid={`series-episode-${v.id}`}
                >
                  <div className="series-info-episode-art">
                    {art ? <img src={art} alt="" loading="lazy" /> : <div className="series-info-no-art">scartlead.tv</div>}
                    <div className="series-info-episode-art-shade" />
                    <span className="series-info-episode-number">
                      {(() => {
                        const runtime = runtimeLabel(v.duration_sec);

                        if (v.season_number != null && v.episode_number != null) {
                          const episodeNumber = `E${String(v.episode_number).padStart(2, "0")}`;
                          const episodeId = Number(v.season_number) === 0
                            ? `SPECIAL · ${episodeNumber}`
                            : `S${String(v.season_number).padStart(2, "0")} · ${episodeNumber}`;

                          return runtime ? `${episodeId} · ${runtime}` : episodeId;
                        }

                        return runtime ? `EPISODE · ${runtime}` : "EPISODE";
                      })()}
                    </span>
                    {current && <span className="series-info-now">● NOW PLAYING</span>}
                    <span className="series-info-play">▶</span>
                  </div>
                  <div className="series-info-episode-copy">
                    <strong>{trimTitle(v.title, showTitle)}</strong>
                    {v.description && <small>{v.description}</small>}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
